/**
 * Represents the layout measurements of a child element within a scroll container.
 */
export interface ChildLayout {
	/** The left offset of the child relative to the scroll container's content. */
	offsetLeft: number;
	/** The width of the child element. */
	offsetWidth: number;
}

/**
 * Represents the scroll state and dimensions of a scroll container.
 */
export interface ScrollContainerLayout {
	/** The current horizontal scroll position. */
	scrollLeft: number;
	/** The visible width of the scroll container. */
	clientWidth: number;
}

/**
 * Calculates how far a scroll container should scroll when clicking a
 * left or right navigation chevron.
 *
 * When scrolling right, the first hidden pill is aligned to the left edge
 * of the container, revealing as many subsequent pills as fit within the
 * container width.
 *
 * When scrolling left, the last hidden pill is aligned to the right edge
 * of the container, revealing as many preceding pills as fit within the
 * container width.
 *
 * @returns A non-negative scroll distance (always ≥ 0).
 */
export function getScrollAmount(
	direction: 'left' | 'right',
	container: ScrollContainerLayout,
	children: ChildLayout[],
): number {
	if (children.length === 0) {
		return 0;
	}

	if (direction === 'right') {
		const containerRight = container.scrollLeft + container.clientWidth;
		// Find pills that are fully or partially hidden to the right
		const hiddenRight = children.filter((child) => {
			const childRight = child.offsetLeft + child.offsetWidth;
			return childRight > containerRight + 1;
		});
		if (hiddenRight.length === 0) {
			return 0;
		}
		// Scroll so the first hidden pill aligns to the left edge of the container,
		// revealing as many of the next hidden pills as fit within the container width.
		const firstHiddenPill = hiddenRight[0];
		return firstHiddenPill.offsetLeft - container.scrollLeft;
	} else {
		const containerLeft = container.scrollLeft;
		// Find pills that are fully or partially hidden to the left
		const hiddenLeft = children.filter((child) => {
			return child.offsetLeft < containerLeft - 1;
		});
		if (hiddenLeft.length === 0) {
			return 0;
		}
		// Scroll so the last hidden pill aligns to the right edge of the container,
		// revealing as many of the previous hidden pills as fit within the container width.
		const lastHiddenPill = hiddenLeft[hiddenLeft.length - 1];
		const lastHiddenPillRight = lastHiddenPill.offsetLeft + lastHiddenPill.offsetWidth;
		return container.scrollLeft + container.clientWidth - lastHiddenPillRight;
	}
}
